import React, { useMemo, useState } from "react";
import "./Settings.css";

// ---- tiny helpers ----
const noop = async () => new Promise((r) => setTimeout(r, 500));
const toTitle = (s) => s.charAt(0).toUpperCase() + s.slice(1);

// Reusable section wrapper
const Section = ({ title, desc, children, actions }) => (
  <section className="set-section">
    <header className="set-section-head">
      <div>
        <h3 className="set-h3">{title}</h3>
        {desc && <p className="set-muted">{desc}</p>}
      </div>
      {actions && <div className="set-actions">{actions}</div>}
    </header>
    <div className="set-section-body">{children}</div>
  </section>
);

// Labeled row
const Row = ({ label, hint, children }) => (
  <div className="set-row">
    <label className="set-label">{label}</label>
    <div className="set-field">
      {children}
      {hint && <p className="set-hint">{hint}</p>}
    </div>
  </div>
);

// Switch
const Switch = ({ checked, onChange, id }) => (
  <button
    type="button"
    id={id}
    role="switch"
    aria-checked={checked}
    onClick={() => onChange(!checked)}
    className={`set-switch ${checked ? "on" : ""}`}
  >
    <span className="knob" />
  </button>
);

// Danger button
const DangerButton = ({ children, ...rest }) => (
  <button className="btn btn-danger" {...rest}>
    {children}
  </button>
);

// Primary button
const PrimaryButton = ({ children, ...rest }) => (
  <button className="btn btn-primary" {...rest}>
    {children}
  </button>
);

// Subtle button
const SubtleButton = ({ children, ...rest }) => (
  <button className="btn btn-subtle" {...rest}>
    {children}
  </button>
);

// Code box
const Code = ({ children }) => <code className="set-code">{children}</code>;

const TABS = [
  "account",
  "organization",
  "billing",
  "notifications",
  "security",
  "api",
  "integrations",
  "appearance",
  "data",
  "danger",
];

export default function Settings() {
  const [active, setActive] = useState("account");
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState("");

  // ---- demo state (replace with fetched values) ----
  const [account, setAccount] = useState({
    name: "Axiomos Admin",
    email: "axiomos2024@gmail.com",
    phone: "123456",
    timezone: "Asia/Kolkata",
  });

  const [org, setOrg] = useState({
    name: "bygging Safety",
    slug: "bygging",
    address: "Delhi, IN",
    domain: "bygging.example.com",
  });

  const [notif, setNotif] = useState({
    email: true,
    sms: false,
    weeklyDigest: true,
    uaucAlerts: true,
  });

  const [sec, setSec] = useState({
    mfa: true,
    sessions: 5,
    sso: false,
  });

  const [api, setApi] = useState({
    key: "sk-live-••••••••••••••••••••••",
    webhooks: [
      { url: "https://example.com/hooks/bygging", secret: "whsec_••••••" },
    ],
  });

  const [theme, setTheme] = useState({
    mode: "system", // light | dark | system
    accent: "#2563eb",
    compact: false,
  });

  const [dataPrefs, setDataPrefs] = useState({
    exportFormat: "csv", // csv | xlsx | json
    includeImages: false,
    redactEmails: true,
  });

  const save = async (what, fn = noop) => {
    setBusy(true);
    try {
      await fn();
      setToast(`${toTitle(what)} saved`);
      setTimeout(() => setToast(""), 1500);
    } finally {
      setBusy(false);
    }
  };

  const ActivePanel = useMemo(() => {
    switch (active) {
      case "account":
        return (
          <>
            <Section
              title="Profile"
              desc="Your personal info used across the app."
              actions={
                <PrimaryButton disabled={busy} onClick={() => save("profile")}>
                  Save changes
                </PrimaryButton>
              }
            >
              <div className="form-grid">
                <Row label="Full name">
                  <input
                    className="set-input"
                    value={account.name}
                    onChange={(e) =>
                      setAccount((s) => ({ ...s, name: e.target.value }))
                    }
                  />
                </Row>
                <Row label="Email">
                  <input
                    className="set-input"
                    type="email"
                    value={account.email}
                    onChange={(e) =>
                      setAccount((s) => ({ ...s, email: e.target.value }))
                    }
                  />
                </Row>
                <Row label="Phone">
                  <input
                    className="set-input"
                    value={account.phone}
                    onChange={(e) =>
                      setAccount((s) => ({ ...s, phone: e.target.value }))
                    }
                  />
                </Row>
                <Row label="Time zone">
                  <select
                    className="set-input"
                    value={account.timezone}
                    onChange={(e) =>
                      setAccount((s) => ({ ...s, timezone: e.target.value }))
                    }
                  >
                    <option>Asia/Kolkata</option>
                    <option>UTC</option>
                    <option>Asia/Dubai</option>
                    <option>Europe/London</option>
                  </select>
                </Row>
              </div>
            </Section>
          </>
        );

      case "organization":
        return (
          <>
            <Section
              title="Organization"
              desc="Company details visible to members and used on documents."
              actions={
                <PrimaryButton
                  disabled={busy}
                  onClick={() => save("organization")}
                >
                  Save changes
                </PrimaryButton>
              }
            >
              <div className="form-grid">
                <Row label="Name">
                  <input
                    className="set-input"
                    value={org.name}
                    onChange={(e) =>
                      setOrg((s) => ({ ...s, name: e.target.value }))
                    }
                  />
                </Row>
                <Row label="Slug" hint="Used in URLs">
                  <input
                    className="set-input"
                    value={org.slug}
                    onChange={(e) =>
                      setOrg((s) => ({ ...s, slug: e.target.value }))
                    }
                  />
                </Row>
                <Row label="Address">
                  <input
                    className="set-input"
                    value={org.address}
                    onChange={(e) =>
                      setOrg((s) => ({ ...s, address: e.target.value }))
                    }
                  />
                </Row>
                <Row label="Custom domain">
                  <input
                    className="set-input"
                    placeholder="your.domain.com"
                    value={org.domain}
                    onChange={(e) =>
                      setOrg((s) => ({ ...s, domain: e.target.value }))
                    }
                  />
                </Row>
              </div>
            </Section>
          </>
        );

      case "billing":
        return (
          <>
            <Section
              title="Billing"
              desc="Manage plan, invoices, and payment method."
              actions={
                <SubtleButton disabled={busy}>
                  Open Customer Portal
                </SubtleButton>
              }
            >
              <div className="billing-box">
                <div>
                  <div className="set-kv">
                    <span>Plan</span>
                    <strong>Pro (Monthly)</strong>
                  </div>
                  <div className="set-kv">
                    <span>Next invoice</span>
                    <strong>₹ 3 on 01 Sep 2025</strong>
                  </div>
                </div>
                <div className="billing-actions">
                  <PrimaryButton disabled={busy}>Change plan</PrimaryButton>
                  <SubtleButton disabled={busy}>Update card</SubtleButton>
                  <SubtleButton disabled={busy}>View invoices</SubtleButton>
                </div>
              </div>
            </Section>
          </>
        );

      case "notifications":
        return (
          <>
            <Section
              title="Notifications"
              desc="Choose what we send and where."
              actions={
                <PrimaryButton
                  disabled={busy}
                  onClick={() => save("notifications")}
                >
                  Save changes
                </PrimaryButton>
              }
            >
              <div className="form-grid">
                <Row label="Email alerts">
                  <Switch
                    checked={notif.email}
                    onChange={(v) => setNotif((s) => ({ ...s, email: v }))}
                  />
                </Row>
                <Row label="SMS alerts">
                  <Switch
                    checked={notif.sms}
                    onChange={(v) => setNotif((s) => ({ ...s, sms: v }))}
                  />
                </Row>
                <Row label="Weekly digest">
                  <Switch
                    checked={notif.weeklyDigest}
                    onChange={(v) =>
                      setNotif((s) => ({ ...s, weeklyDigest: v }))
                    }
                  />
                </Row>
                <Row label="UA/UC escalations">
                  <Switch
                    checked={notif.uaucAlerts}
                    onChange={(v) => setNotif((s) => ({ ...s, uaucAlerts: v }))}
                  />
                </Row>
              </div>
            </Section>
          </>
        );

      case "security":
        return (
          <>
            <Section
              title="Security"
              desc="Protect your account and workspace."
              actions={
                <PrimaryButton disabled={busy} onClick={() => save("security")}>
                  Save changes
                </PrimaryButton>
              }
            >
              <div className="form-grid">
                <Row label="Multi-factor auth">
                  <Switch
                    checked={sec.mfa}
                    onChange={(v) => setSec((s) => ({ ...s, mfa: v }))}
                  />
                </Row>
                <Row label="Max concurrent sessions" hint="0 = unlimited">
                  <input
                    className="set-input"
                    type="number"
                    min="0"
                    value={sec.sessions}
                    onChange={(e) =>
                      setSec((s) => ({
                        ...s,
                        sessions: Number(e.target.value),
                      }))
                    }
                  />
                </Row>
                <Row label="SSO (SAML/OIDC)">
                  <Switch
                    checked={sec.sso}
                    onChange={(v) => setSec((s) => ({ ...s, sso: v }))}
                  />
                </Row>
              </div>
            </Section>
          </>
        );

      case "api":
        return (
          <>
            <Section
              title="API & Webhooks"
              desc="Programmatic access and event delivery."
              actions={
                <>
                  <SubtleButton
                    disabled={busy}
                    onClick={() => save("api key rotate")}
                  >
                    Rotate key
                  </SubtleButton>
                  <PrimaryButton disabled={busy} onClick={() => save("api")}>
                    Save changes
                  </PrimaryButton>
                </>
              }
            >
              <Row label="Secret key">
                <input className="set-input" value={api.key} readOnly />
              </Row>

              <div className="set-divider" />

              <div className="set-table">
                <div className="set-table-head">
                  <div>Webhook URL</div>
                  <div>Secret</div>
                  <div />
                </div>
                {api.webhooks.map((w, i) => (
                  <div className="set-table-row" key={i}>
                    <div>
                      <Code>{w.url}</Code>
                    </div>
                    <div>
                      <Code>{w.secret}</Code>
                    </div>
                    <div className="row-end">
                      <SubtleButton
                        onClick={() =>
                          setApi((s) => ({
                            ...s,
                            webhooks: s.webhooks.filter((_, idx) => idx !== i),
                          }))
                        }
                      >
                        Remove
                      </SubtleButton>
                    </div>
                  </div>
                ))}
              </div>

              <div className="webhook-add">
                <input
                  className="set-input"
                  placeholder="https://your.app/hooks/..."
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && e.currentTarget.value.trim()) {
                      setApi((s) => ({
                        ...s,
                        webhooks: [
                          ...s.webhooks,
                          {
                            url: e.currentTarget.value.trim(),
                            secret: "whsec_new",
                          },
                        ],
                      }));
                      e.currentTarget.value = "";
                    }
                  }}
                />
                <SubtleButton
                  onClick={(e) => {
                    const input = e.currentTarget.previousSibling;
                    if (input && input.value.trim()) {
                      setApi((s) => ({
                        ...s,
                        webhooks: [
                          ...s.webhooks,
                          { url: input.value.trim(), secret: "whsec_new" },
                        ],
                      }));
                      input.value = "";
                    }
                  }}
                >
                  Add
                </SubtleButton>
              </div>
            </Section>
          </>
        );

      case "integrations":
        return (
          <>
            <Section
              title="Integrations"
              desc="Connect with your favorite tools."
            >
              <div className="cards">
                {[
                  {
                    name: "Slack",
                    desc: "Alerts & notifications",
                    connected: true,
                  },
                  {
                    name: "Microsoft Teams",
                    desc: "Channel notifications",
                    connected: false,
                  },
                  {
                    name: "Power BI",
                    desc: "Analytics & dashboards",
                    connected: false,
                  },
                ].map((x) => (
                  <div key={x.name} className="card">
                    <h4>{x.name}</h4>
                    <p className="set-muted">{x.desc}</p>
                    {x.connected ? (
                      <SubtleButton>Manage</SubtleButton>
                    ) : (
                      <PrimaryButton>Connect</PrimaryButton>
                    )}
                  </div>
                ))}
              </div>
            </Section>
          </>
        );

      case "appearance":
        return (
          <>
            <Section
              title="Appearance"
              desc="Theme and layout preferences."
              actions={
                <PrimaryButton
                  disabled={busy}
                  onClick={() => save("appearance")}
                >
                  Save changes
                </PrimaryButton>
              }
            >
              <div className="form-grid">
                <Row label="Theme mode">
                  <select
                    className="set-input"
                    value={theme.mode}
                    onChange={(e) =>
                      setTheme((s) => ({ ...s, mode: e.target.value }))
                    }
                  >
                    <option value="system">System</option>
                    <option value="light">Light</option>
                    <option value="dark">Dark</option>
                  </select>
                </Row>
                <Row label="Accent color">
                  <input
                    className="set-input"
                    type="color"
                    value={theme.accent}
                    onChange={(e) =>
                      setTheme((s) => ({ ...s, accent: e.target.value }))
                    }
                    style={{ padding: 0, height: 42, width: 68 }}
                  />
                </Row>
                <Row label="Compact density">
                  <Switch
                    checked={theme.compact}
                    onChange={(v) => setTheme((s) => ({ ...s, compact: v }))}
                  />
                </Row>
              </div>
            </Section>
          </>
        );

      case "data":
        return (
          <>
            <Section
              title="Data & Export"
              desc="Export, import, and compliance options."
              actions={
                <PrimaryButton
                  disabled={busy}
                  onClick={() => save("data preferences")}
                >
                  Save preferences
                </PrimaryButton>
              }
            >
              <div className="form-grid">
                <Row label="Export format">
                  <select
                    className="set-input"
                    value={dataPrefs.exportFormat}
                    onChange={(e) =>
                      setDataPrefs((s) => ({
                        ...s,
                        exportFormat: e.target.value,
                      }))
                    }
                  >
                    <option value="csv">CSV</option>
                    <option value="xlsx">Excel (.xlsx)</option>
                    <option value="json">JSON</option>
                  </select>
                </Row>
                <Row label="Include images">
                  <Switch
                    checked={dataPrefs.includeImages}
                    onChange={(v) =>
                      setDataPrefs((s) => ({ ...s, includeImages: v }))
                    }
                  />
                </Row>
                <Row label="Redact emails">
                  <Switch
                    checked={dataPrefs.redactEmails}
                    onChange={(v) =>
                      setDataPrefs((s) => ({ ...s, redactEmails: v }))
                    }
                  />
                </Row>
              </div>

              <div className="set-divider" />

              <div className="export-actions">
                <PrimaryButton disabled={busy} onClick={() => save("export")}>
                  Export now
                </PrimaryButton>
                <SubtleButton disabled={busy}>Import data</SubtleButton>
              </div>
            </Section>
          </>
        );

      case "danger":
        return (
          <>
            <Section
              title="Danger Zone"
              desc="These actions are destructive and cannot be undone."
            >
              <div className="danger-grid">
                <div>
                  <h4>Delete organization</h4>
                  <p className="set-muted">
                    Permanently remove all data for <strong>{org.name}</strong>.
                  </p>
                </div>
                <DangerButton
                  disabled={busy}
                  onClick={() => save("delete organization")}
                >
                  Delete
                </DangerButton>
              </div>
            </Section>
          </>
        );

      default:
        return null;
    }
  }, [active, account, org, notif, sec, api, theme, dataPrefs, busy]);

  return (
    <div className="settings-wrap">
      <aside className="settings-aside">
        <h2 className="set-title">Settings</h2>
        <nav className="set-nav">
          {TABS.map((t) => (
            <button
              key={t}
              className={`set-tab ${active === t ? "active" : ""}`}
              onClick={() => setActive(t)}
            >
              {toTitle(t)}
            </button>
          ))}
        </nav>
      </aside>

      <main className="settings-main">
        {toast && <div className="set-toast">{toast}</div>}
        {ActivePanel}
      </main>
    </div>
  );
}
